import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useGetDeviceListingQuery,
  useLazyGetDeviceListingQuery,
} from "../../../api/network/camerasAdminVMApiService";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { CameraListingPayload } from "../../../api/types/Camera";
import { TLegacyGroup } from "../../../api/types/Group";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldCheckboxWithOnOffLabels,
  AdminFormFieldPillItem,
  AdminFormFieldPillList,
  AdminFormFieldSubmit,
  PillItem,
} from "../../../components/admin/formFields";
import AssetsSelector from "../../../components/common/AssetsSelector";
import FTModal from "../../../components/common/FTModal";
import { TGroupSelectorItem } from "../../../components/common/groupSelector.type";
import HierarchyGroupSelector from "../../../components/common/hierarchyGroupSelector";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { useLoggedInUserData } from "../../../utils/user";
import { REPORT_FILTER_BY_OPTION_VALUES } from "../constants";
import { useCameraAdminContext } from "../context/cameraAdmin.context";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";

interface ModalGroupCameraSelectorProps {
  hideGroupCameraToggle: boolean;
  allGroups: TLegacyGroup[];
  filteredGroupData: TGroupSelectorItem[];
  setFilteredGroupData: (groupData: TGroupSelectorItem[]) => void;
  isConfigurationModal?: boolean;
  navigateToSettings?: (
    selectedType: string,
    camera_ids: string[],
    group_ids: string[],
    isAllCameraSelected: boolean,
    isAllGroupSelected: boolean
  ) => void;
}

interface CheckboxData {
  id: string;
  label: string;
  level: string;
  checked: boolean;
}

const debugComponent = false;

const ModalGroupCameraSelector: FC<ModalGroupCameraSelectorProps> = ({
  hideGroupCameraToggle = false,
  allGroups,
  filteredGroupData,
  setFilteredGroupData,
  isConfigurationModal = false,
  navigateToSettings,
}) => {
  const {
    selectedGroups,
    selectedDataType,
    setSelectedDataType,
    allGroupsSelected,
    setAllGroupsSelected,
    allCamerasSelected,
    setAllCamerasSelected,
    selectedCameras,
    cameraSearchKeyword,
    setCameraSearchKeyword,
    setSelectedCameras,
  } = useCameraAdminContext();
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.groupCameraSelector",
  });

  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";
  const [groupSearchInput, setGroupSearchInput] = useState("");
  const [tempAllGroupSelected, setTempAllGroupSelected] =
    useState<boolean>(filteredGroupData.length > 0 &&
      filteredGroupData.every((group) => group.checked));
  // All cameras are selected by default when there is no prior explicit selection
  const [tempAllCameraSelected, setTempAllCameraSelected] = useState<boolean>(
    allCamerasSelected || !selectedCameras?.length
  );
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  useEffect(() => {
    if (!modalsState.showGroupCameraSelector) {
      setGroupSearchInput("");
      setCurrentPage(1);
    }
  }, [modalsState.showGroupCameraSelector]);
  const [groupCameraToggled, setGroupCameraToggled] = useState(true);
  const [checkedCamerasState, setCheckedCamerasState] = useState<
    Record<string, boolean>
  >({});
  const [deselectedCameraIds, setDeselectedCameraIds] = useState<string[]>([]);

  // "Select All" is only shown as checked while nothing has been deselected
  const isSelectAllChecked =
    tempAllCameraSelected && deselectedCameraIds.length === 0;

  // Every time the modal opens: select all cameras by default, unless the user already confirmed an explicit subset earlier
  useEffect(() => {
    if (!modalsState.showGroupCameraSelector) return;
    const hasPriorExplicitSelection =
      !allCamerasSelected && !!selectedCameras?.length;
    setDeselectedCameraIds([]);
    if (hasPriorExplicitSelection) {
      setTempAllCameraSelected(false);
      setCheckedCamerasState(
        (selectedCameras ?? []).reduce<Record<string, boolean>>(
          (state, cameraId) => {
            state[cameraId] = true;
            return state;
          },
          {}
        )
      );
    } else {
      setTempAllCameraSelected(true);
      setCheckedCamerasState({});
    }
  }, [modalsState.showGroupCameraSelector]);
  const dispatch = useDispatch();
  const [cameraManagementQueryParams, setCameraManagementQueryParams] =
    useState<CameraListingPayload>({
      keyword: "",
      model: "",
      status: "",
      offset: 0,
      limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      ignore_not_initiated: isConfigurationModal === true,
    });
  const [getDeviceListing] = useLazyGetDeviceListingQuery();
  const [isLoadingAllCameraIds, setIsLoadingAllCameraIds] = useState(false);

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");

  const {
    data: dataDeviceListing,
    isFetching: isFetchingDeviceListing,
  } = useGetDeviceListingQuery(
    {
      ...cameraManagementQueryParams,
      // search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.ALL_FLAG, // TODO: search_fields to be implemented for camera listing API
      offset: cameraManagementQueryParams.offset,
    },
    {
      refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
    }
  );

  const { count, data: results } = dataDeviceListing || {};
  // memoized so the reference is stable while `results` is unchanged
  const allCameras = useMemo(() => results ?? [], [results]);
  const [camerasData, setCamerasData] = useState<CheckboxData[]>([]);

  useEffect(() => {
    if (allCameras && allCameras.length > 0) {
      const updatedCamerasData = allCameras.map((camera: any) => ({
        id: camera.serial_number,
        label: `${consistentDisplayAssetItem(camera.ft_device_id, camera.ft_device_description, camera.ft_displayname, camera.drivername, showDriver)}`,
        // label: `${camera.serial_number}${!!camera.ft_device_id ? ` | ${camera.ft_device_id}` : ""}${camera.ft_device_description ? ` | ${camera.ft_device_description}` : ""}${camera.displayname ? ` | ${camera.displayname}` : ""}`,

        level: camera.serial_number || "",
        checked: tempAllCameraSelected
          ? !deselectedCameraIds.includes(camera.serial_number)
          : checkedCamerasState[camera.serial_number] || false,
      }));
      setCamerasData(updatedCamerasData);
    } else if (!isFetchingDeviceListing) {
      setCamerasData((previous) => (previous.length === 0 ? previous : []));
    }
  }, [
    allCameras,
    checkedCamerasState,
    deselectedCameraIds,
    tempAllCameraSelected,
    isFetchingDeviceListing,
  ]);

  // pre-select cameras as applicable
  useEffect(() => {
    if (
      isConfigurationModal &&
      modalsState.showGroupCameraSelector &&
      allCameras.length > 0
    ) {
      setCheckedCamerasState((previousState) => {
        const updatedState = { ...previousState };
        selectedCameras?.forEach((cameraId) => {
          if (updatedState[cameraId] === undefined) {
            updatedState[cameraId] = true;
          }
        });
        allCameras.forEach((camera: any) => {
          if (updatedState[camera.serial_number] === undefined) {
            updatedState[camera.serial_number] =
              selectedCameras?.includes(camera.serial_number) || false;
          }
        });
        return updatedState;
      });
    }
  }, [
    isConfigurationModal,
    modalsState.showGroupCameraSelector,
    allCameras,
    selectedCameras,
  ]);

  const handleCheckboxChange = (id: string) => {
    if (tempAllCameraSelected) {
      setDeselectedCameraIds((previousIds) =>
        previousIds.includes(id)
          ? previousIds.filter((cameraId) => cameraId !== id)
          : [...previousIds, id]
      );
      return;
    }

    setCheckedCamerasState((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };
  const handleSelectAllCamerasChange = () => {
    const newSelectAll = !isSelectAllChecked;
    setTempAllCameraSelected(newSelectAll);
    setDeselectedCameraIds([]);
    setCheckedCamerasState(
      newSelectAll
        ? allCameras.reduce<Record<string, boolean>>((selectedState, camera: any) => {
          selectedState[camera.serial_number] = true;
          return selectedState;
        }, {})
        : {}
    );
  };
  const [isModified, setIsModified] = useState<boolean>(false);
  const [tempSelected, setTempSelected] =
    useState<REPORT_FILTER_BY_OPTION_VALUES>(
      REPORT_FILTER_BY_OPTION_VALUES.CAMERA
    );
  const [tempFilteredGroupData, setTempFilteredGroupData] = useState<
    TGroupSelectorItem[]
  >(
    allGroups.map((group) => ({
      ...group,
      is_hgroup: false,
      checked: true,
    }))
  );

  // Reset to the last confirmed state every time the modal opens, so a
  // cancelled or abandoned session never leaks into the next one.
  useEffect(() => {
    if (!modalsState.showGroupCameraSelector) return;
    const committedGroups: TGroupSelectorItem[] =
      filteredGroupData.length > 0
        ? filteredGroupData
        : allGroups.map((group) => ({
            ...group,
            is_hgroup: false,
            checked: true,
          }));
    setTempFilteredGroupData(committedGroups);
    setTempAllGroupSelected(committedGroups.every((group) => group.checked));
    setIsModified(false);
    setGroupSearchInput("");
    setCameraSearchKeyword("");
    setCurrentPage(1);
  }, [modalsState.showGroupCameraSelector]);

  const handleSubmit = async () => {
    setAllGroupsSelected(tempAllGroupSelected);
    if (tempSelected === REPORT_FILTER_BY_OPTION_VALUES.CAMERA) {
      let allCamerasRemainSelected = tempAllCameraSelected;
      let selectedCameraIds: string[];

      if (tempAllCameraSelected && deselectedCameraIds.length > 0) {
        setIsLoadingAllCameraIds(true);
        try {
          const limit = APP_CONFIG.LISTINGS.XL_PAGE_SIZE;
          const baseParams = {
            ...cameraManagementQueryParams,
            keyword: "",
            limit,
          };

          // second argument = preferCacheValue, so cached pages are reused
          const firstPage = await getDeviceListing(
            { ...baseParams, offset: 0 },
            true
          ).unwrap();

          // fetch all remaining pages in parallel instead of one after another
          const remainingOffsets: number[] = [];
          for (let offset = limit; offset < firstPage.count; offset += limit) {
            remainingOffsets.push(offset);
          }
          const remainingPages = await Promise.all(
            remainingOffsets.map((offset) =>
              getDeviceListing({ ...baseParams, offset }, true).unwrap()
            )
          );

          const excludedIds = new Set(deselectedCameraIds);
          selectedCameraIds = [firstPage, ...remainingPages]
            .flatMap((page) =>
              page.data.map((camera) => camera.serial_number)
            )
            .filter((cameraId) => !excludedIds.has(cameraId));
          allCamerasRemainSelected = false;
        } catch (error) {
          console.error("Failed to load camera IDs for selection:", error);
          toast.error("Unable to load the camera list. Please try again.");
          return;
        } finally {
          setIsLoadingAllCameraIds(false);
        }
      } else if (tempAllCameraSelected) {
        selectedCameraIds = [];
      } else {
        selectedCameraIds = Object.keys(checkedCamerasState).filter(
          (cameraId) => checkedCamerasState[cameraId]
        );
      }

      if (!allCamerasRemainSelected && selectedCameraIds.length === 0) {
        toast.error(t("atleast_one_camera"));
        return;
      }

      setSelectedDataType(tempSelected);
      setAllCamerasSelected(allCamerasRemainSelected);
      setSelectedCameras(selectedCameraIds);
      if (isConfigurationModal) {
        navigateToSettings?.(
          tempSelected,
          selectedCameraIds,
          [],
          allCamerasRemainSelected,
          tempAllGroupSelected
        );
      }
    } else {
      if (isConfigurationModal) {
        const selectedGroupData = tempFilteredGroupData
          .filter((item) => item.checked)
          .map((group) => group.groupid);
        navigateToSettings?.(
          tempSelected,
          [],
          selectedGroupData,
          tempAllCameraSelected,
          tempAllGroupSelected
        );
      }
      if (
        tempFilteredGroupData.filter((item) => item.checked).length === 0 &&
        // !allGroupsSelected
        !tempAllGroupSelected
      )
        return;
      setFilteredGroupData(tempFilteredGroupData);
      setAllCamerasSelected(tempAllCameraSelected);
      // TODO: Check if following should be disabled
      //   const selectedGroupData = tempFilteredGroupData
      //     .filter((item) => item.checked)
      //     .map((group) => group.groupid);
      //   setSelectedCamera(selectedGroupData);
    }
    setSelectedDataType(tempSelected);
    setTimeout(() => {
      hideModal();
    }, 300);
  };
  const searchKeyword = groupSearchInput.trim().toLowerCase();
  const searchedGroupData = tempFilteredGroupData.filter((group) =>
    [group.groupid, group.description].some((value) =>
      String(value ?? "").toLowerCase().includes(searchKeyword)
    )
  );
  const paginatedData = searchedGroupData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handlePageChange = (data: TPaginationSelected) => {
    setCurrentPage(data.selected + 1); // Update the current page
  };

useEffect(() => {
  if (accountHasGroupHierarchyEnabled === "1") return;
  if (debugComponent)
    console.log("[UPDATE]", "setting on change of filteredGroupData");
  if (!isModified && filteredGroupData.length > 0) {
    setTempFilteredGroupData(filteredGroupData);
    setTempAllGroupSelected(filteredGroupData.every((group) => group.checked));
  }
}, [filteredGroupData]);

  // reset camera search whenever the modal is closed or opened
  useEffect(() => {
    debouncedSetSearchKeyword.cancel();
    setCameraSearchKeyword("");
    setCameraManagementQueryParams((previousParams) =>
      previousParams.keyword === "" && previousParams.offset === 0
        ? previousParams
        : { ...previousParams, keyword: "", offset: 0 }
    );
  }, [modalsState.showGroupCameraSelector]);

  // const handleAllCheckboxChange = (e: any) => {
  //   setAllGroupsSelected(e.target.checked);
  //   // on if checked, select all groups on page
  //   if (!e.target.checked) return;
  //   if (debugComponent)
  //     console.log("[UPDATE]", "setting on change of all checkbox");
  //   setTempFilteredGroupData(
  //     tempFilteredGroupData.map((group) => ({
  //       ...group,
  //       checked: e.target.checked,
  //     }))
  //   );
  // };
  const handleAllCheckboxChange = (e: any) => {
    // setAllGroupsSelected(e.target.checked);
    setTempAllGroupSelected(e.target.checked);
    // disabled the line below to allow checkboxes to sync with 'Select All' checkbox
    // if (!e.target.checked) return; // on if checked, select all groups on page
    if (debugComponent)
      console.log("[UPDATE]", "setting on change of all checkbox");
    // Update the `checked` status for all groups
    setTempFilteredGroupData(
      tempFilteredGroupData.map((group) => ({
        ...group,
        checked: e.target.checked, // True or false based on the checkbox status
      }))
    );
    setIsModified(true);
    if (debugComponent) {
      console.log(
        "[UPDATE]",
        `Setting all groups to ${e.target.checked ? "selected" : "deselected"}`
      );
    }
  };

  const hideModal = (initiator?: "cross-button" | undefined) => {
    if (!isConfigurationModal || initiator === "cross-button") {
      setCameraManagementQueryParams({
        ...cameraManagementQueryParams,
        offset: 0,
      });
      dispatch(
        setModalsData({ ...modalsState, showGroupCameraSelector: false })
      );
      return;
    }
    if (groupCameraToggled) {
      if (
        Object.values(checkedCamerasState).filter((item: boolean) => item)
          .length === 0 &&
        !tempAllCameraSelected
      ) {
        toast.error(t("atleast_one_camera"));
        return;
      }
    } else {
      if (
        tempFilteredGroupData.filter((item) => item.checked).length === 0 &&
        !tempAllGroupSelected
      ) {
        toast.error(t("atleast_one_group"));
        return;
      }
    }
    setCameraManagementQueryParams({
      ...cameraManagementQueryParams,
      offset: 0,
    });

  };

const handleGroupSearch = (searchTerm: string) => {
  const normalizedSearchTerm = searchTerm === "clear" ? "" : searchTerm;
  setGroupSearchInput(normalizedSearchTerm);
  setCurrentPage(1);
};

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setCameraSearchKeyword(value);
    // if search term is empty, select all assets on page
    // if (value.length === 0) {
    //   setCheckedAssetsState((prevState) => {
    //     const updatedState: any = {};
    //     allAssets.forEach((asset: any) => {
    //       updatedState[asset.deviceid] = true;
    //     });
    //     return updatedState;
    //   });
    //   const updatedAssetsData = allAssets.map((asset: any) => ({
    //     id: asset.deviceid,
    //     label: asset.displayname,
    //     level: asset.level || "",
    //     checked: true,
    //   }));
    //   setAssetsData(updatedAssetsData);
    // }

    setCameraManagementQueryParams((previousParams) => ({
      ...previousParams,
      offset: 0,
      keyword: value,
    }));
  }, 500);

  const handleTypeToggleChange = () => {
    if (groupCameraToggled) {
      setGroupCameraToggled(false);
      //setSelectedDataType(REPORT_FILTER_BY_OPTION_VALUES.GROUP);
      setTempSelected(REPORT_FILTER_BY_OPTION_VALUES.GROUP);
    } else {
      setGroupCameraToggled(true);
      //setSelectedDataType(REPORT_FILTER_BY_OPTION_VALUES.ASSET);
      setTempSelected(REPORT_FILTER_BY_OPTION_VALUES.CAMERA);
    }
  };

  useEffect(() => {
    setCameraManagementQueryParams((prev) => ({
      ...prev,
      ignore_not_initiated: isConfigurationModal === true,
      offset: 0,
      keyword: "",
    }));
    // handleSelectAllAssetsChange();
    debouncedSetSearchKeyword("");
  }, [
    isConfigurationModal,
    // groupCameraToggled,
    cameraManagementQueryParams.limit,
  ]);

  //unmount cleanup all states when modal is used from different places/components/routes
  useEffect(() => {
    return () => {
      setTempFilteredGroupData(
        allGroups.map((group) => ({
          ...group,
          is_hgroup: false,
          checked: true,
        }))
      );
      setTempSelected(REPORT_FILTER_BY_OPTION_VALUES.CAMERA);
      setGroupCameraToggled(true);
      setGroupSearchInput("");
      setCameraSearchKeyword("");
      setAllGroupsSelected(false);
      // NOTE: setAllCamerasSelected(false) intentionally removed — it reset the
      // context to "not all selected" on every unmount, undoing the default-all state.
      setCheckedCamerasState({});
      setDeselectedCameraIds([]);
      setCurrentPage(1);
      setIsModified(false);
    };
  }, []);

  return (
    <>
      <FTModal
        visible={modalsState.showGroupCameraSelector}
        heading={
          isConfigurationModal
            ? t("camera_configuration")
            : !groupCameraToggled
              ? t("heading")
              : t("heading_cameras")
        }
        onHide={() => hideModal("cross-button")}
        containerStyle="max-w-[calc(100vw-4rem)] w-[470px]"
        dismissible={true}
      >
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="text-[8px]">
            allGroupsSelected: {allGroupsSelected.toString()} /
            tempAllGroupSelected: {tempAllGroupSelected.toString()}
            <br />
            allCamerasSelected: {allCamerasSelected.toString()} /
            tempAllCameraSelected: {tempAllCameraSelected.toString()}
            <br />
          </pre>
        )}
        <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
          <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
            {t(
              isConfigurationModal
                ? "sub_heading_camera_configuration"
                : !groupCameraToggled
                  ? "sub_heading_group"
                  : "sub_heading_camera"
            )}
          </p>
          <div className="flex justify-between col-span-6 gap-4">
            {!groupCameraToggled ? (
              <>
                {accountHasGroupHierarchyEnabled === "1" ? (
                  <span className="flex-grow"></span>
                ) : (
                  <AppSearchBox
                    wrapperClassName="flex-grow"
                    placeholder={tMain("search_groups")}
                    controlledValue={groupSearchInput}
                    onChange={(e) => handleGroupSearch(e.target.value)}
                    onClear={() => handleGroupSearch("clear")}
                  />
                )}
              </>
            ) : (
              <AppSearchBox
                key={`search_${modalsState.showGroupCameraSelector}`}
                wrapperClassName="flex-grow"
                placeholder={tMain("search_cameras")}
                initialValue={cameraManagementQueryParams.keyword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  debouncedSetSearchKeyword(e.target.value)
                }
                onClear={() => debouncedSetSearchKeyword("")}
                loading={isFetchingDeviceListing || isLoadingAllCameraIds}
              />
            )}
            {!hideGroupCameraToggle && (
              <AdminFormFieldCheckboxWithOnOffLabels
                // customWrapperClass="w-40"
                offLabel={tMain("group_camera_toggle_label_on")}
                onLabel={tMain("group_camera_toggle_label_off")}
                label=""
                id="time_toggle"
                type="checkbox"
                name="time_toggle"
                onChange={handleTypeToggleChange}
                checked={groupCameraToggled}
              />
            )}
          </div>
          {!groupCameraToggled ? (
            <div
              className={`relative col-span-6 ${accountHasGroupHierarchyEnabled === "1" && !groupCameraToggled
                  ? "h-[calc(100vh-480px)]"
                  : "h-[calc(100vh-480px)]"
                } overflow-y-auto`}
            >
              <div className="absolute w-full h-full overflow-auto grid grid-cols-6 gap-2 content-start">
                <div className="col-span-6 sticky top-0 bg-white z-[11]">
                  <AdminFormFieldCheckbox
                    id="all"
                    label="Select All"
                    type="checkbox"
                    name="all"
                    // checked={allGroupsSelected}
                    checked={tempAllGroupSelected}
                    onChange={handleAllCheckboxChange}
                    disabled={!!groupSearchInput}
                    // key={`all_groups_${allGroupsSelected.toString()}`}
                    key={`all_groups_${tempAllGroupSelected.toString()}`}
                  />
                </div>

                <React.Fragment
                  // key={`groups_list_${allGroupsSelected.toString()}`}
                  key={`groups_list_${tempAllGroupSelected.toString()}`}
                >
                  {accountHasGroupHierarchyEnabled === "1" ? (
                    <>
                      <div className="sticky col-span-6 top-16 z-10">
                        <span className="absolute w-full h-16 bg-white -top-2 -mt-[3px] z-10" />
                        <AdminFormFieldSubmit
                          label={tMain("select_groups_button")}
                          type="button"
                          variant="primary-like"
                          customWrapperClass="w-full relative z-10"
                          onClick={() => {
                            dispatch(
                              setModalsData({
                                ...modalsState,
                                showHierarchyGroupSelector: true,
                              })
                            );
                          }}
                          // disabled={!!allGroupsSelected} // disable if user cannot edit or all groups is selected
                          disabled={!!tempAllGroupSelected}
                        />
                      </div>
                      <div className="col-span-6 sticky top-[61px] z-10 grid grid-cols-6">
                        {/* modal moved below out of this FTModal to fix z-index issue */}
                      </div>
                      {/* <pre className="col-span-6 text-[8px] h-24 overflow-auto">
                      tempFilteredGroupData (truncated):
                      {JSON.stringify(
                        tempFilteredGroupData.map((item) => item.groupid),
                        null,
                        2
                      )}
                    </pre> */}
                      <div className="col-span-6">
                        <AdminFormFieldPillList
                          // disabled={!!allGroupsSelected}
                          disabled={!!tempAllGroupSelected}
                        // isError={errors.selected_groups && userCanEdit}
                        >
                          {tempFilteredGroupData
                            .filter((item) => item.checked)
                            .map((group: TGroupSelectorItem, i: number) => (
                              <AdminFormFieldPillItem // TODO: Fix prefill on change item from left column
                                key={`pillItem_${i}`}
                                // disabled={!!allGroupsSelected}
                                disabled={!!tempAllGroupSelected}
                                item={{ name: group.groupid } as PillItem}
                                onRemove={() => {
                                  console.log(
                                    "[UPDATE]",
                                    "setting on remove pill item"
                                  );
                                  setTempFilteredGroupData(
                                    tempFilteredGroupData.map((grp) => {
                                      if (grp.groupid === group.groupid) {
                                        return { ...grp, checked: false };
                                      }
                                      return grp;
                                    })
                                  );
                                }}
                              // onItemClick={(item) => {
                              //   dispatch(
                              //     setModalsData({
                              //       ...modalsState,
                              //       showReadOnlyViewForHierarchyGroup: {
                              //         accountid: thisUserOrganizationId,
                              //         active_inactive_vehicle_counts: {
                              //           active_vehicle_count: 0,
                              //           inactive_vehicle_count: 0,
                              //         },
                              //         description: "",
                              //         groupid: group,
                              //       } as OrgGHGroup,
                              //       showHierarchyGroupSelector: true,
                              //     })
                              //   );
                              // }}
                              />
                            ))}
                        </AdminFormFieldPillList>
                      </div>
                    </>
                  ) : (
                    <>
                      {paginatedData.map(
                        (group: TGroupSelectorItem, index: number) => (
                          <div className="col-span-6" key={index}>
                            <AdminFormFieldCheckbox
                              id={group.groupid}
                              label={group.description ? `${group.groupid} | ${group.description}` : group.groupid}
                              type="checkbox"
                              name={group.groupid}
                              checked={group.checked}
                              onChange={() => {
                                const updatedGroups = tempFilteredGroupData.map((grp) =>
                                  grp.groupid === group.groupid
                                    ? { ...grp, checked: !grp.checked }
                                    : grp
                                );
                                setTempFilteredGroupData(updatedGroups);
                                setIsModified(true);
                                // Select All follows the data: on only when every group is checked
                                setTempAllGroupSelected(
                                  updatedGroups.length > 0 && updatedGroups.every((grp) => grp.checked)
                                );
                              }}
                            // disabled={allGroupsSelected}
                            />
                          </div>
                        )
                      )}
                    </>
                  )}
                </React.Fragment>
              </div>
            </div>
          ) : (
            <AssetsSelector
              data={camerasData}
              // selectAll={allAssetsSelected}
              selectAll={isSelectAllChecked}
              handleSelectAllChange={handleSelectAllCamerasChange}
              handleCheckboxChange={handleCheckboxChange}
              assetSearchKeyword={cameraSearchKeyword}
              setAssetSearchKeyword={setCameraSearchKeyword}
              loading={isFetchingDeviceListing}
              allowIndividualSelectionWhenSelectAll={true}
              selectorItemTitleFormatter={(item: CheckboxData) => item.label}
              allAssetsOptionEnabled={true}
            />
          )}
          {(accountHasGroupHierarchyEnabled !== "1" || groupCameraToggled) && (
            <>
              {!groupCameraToggled ? (
                <Pagination
                  pageSize={PAGE_SIZE}
                  totalPages={Math.max(
                    1,
                    Math.ceil(searchedGroupData.length / PAGE_SIZE)
                  )}
                  forcePage={currentPage - 1}
                  handlePageClick={handlePageChange}
                  onlyPageChange={true}
                  size={1}
                  wrapperClass="col-span-6"
                />
              ) : (
                <Pagination
                  pageSize={cameraManagementQueryParams.limit}
                  onlyPageChange={true}
                  totalPages={
                    count
                      ? Math.ceil(count / cameraManagementQueryParams.limit)
                      : 1
                  }
                  forcePage={
                    !!cameraManagementQueryParams.offset
                      ? Math.floor(
                        cameraManagementQueryParams.offset /
                        cameraManagementQueryParams.limit
                      )
                      : 0
                  }
                  handlePageClick={(data: TPaginationSelected) => {
                    setCameraManagementQueryParams({
                      ...cameraManagementQueryParams,
                      offset: data.selected * cameraManagementQueryParams.limit,
                    });
                  }}
                  paginCurrentPage={
                    !!cameraManagementQueryParams.offset
                      ? Math.floor(
                        cameraManagementQueryParams.offset /
                        cameraManagementQueryParams.limit
                      ) + 1
                      : 1
                  }
                  paginItemsOnCurrentPage={results?.length || 0}
                  paginTotalItemsCount={count}
                  size={1}
                  wrapperClass="col-span-6"
                />
              )}
            </>
          )}
          {!groupCameraToggled ? (
            <>
              <AdminFormFieldSubmit
                label={
                  tempFilteredGroupData.filter((item) => item.checked)
                    .length ===
                    // 0 && !allGroupsSelected
                    0 && !tempAllGroupSelected
                    ? t("atleast_one_group")
                    : tMain("confirm")
                }
                type="button"
                variant={
                  tempFilteredGroupData.filter((item) => item.checked)
                    .length ===
                    // 0 && !allGroupsSelected
                    0 && !tempAllGroupSelected
                    ? "danger"
                    : "primary"
                }
                onClick={handleSubmit}
                disabled={
                  tempFilteredGroupData.filter((item) => item.checked)
                    .length ===
                  // 0 && !allGroupsSelected
                  0 && !tempAllGroupSelected
                }
              />

              <AdminFormFieldSubmit
                customWrapperClass={`col-span-6${tempFilteredGroupData.filter((item) => item.checked)
                    .length === 0
                    ? " hidden"
                    : ""
                  }`}
                label={tMain("cancel")}
                type="button"
                variant="danger-transparent"
                onClick={() => {
                  if (debugComponent)
                    console.log("[UPDATE]", "setting on cancel");
                   setTempFilteredGroupData(filteredGroupData);
                   setTempAllGroupSelected(filteredGroupData.length > 0 &&
                      filteredGroupData.every((group) => group.checked)
                  );
                  setIsModified(false);
                  hideModal();
                }}
              />
            </>
          ) : (
            <>
              <AdminFormFieldSubmit
                label={
                  Object.values(checkedCamerasState).filter(
                    (item: boolean) => item
                    // ).length === 0 && !allAssetsSelected
                  ).length === 0 && !tempAllCameraSelected
                    ? t("atleast_one_camera")
                    : tMain("confirm")
                }
                type="button"
                variant={
                  Object.values(checkedCamerasState).filter(
                    (item: boolean) => item
                    // ).length === 0 && !allAssetsSelected
                  ).length === 0 && !tempAllCameraSelected
                    ? "danger"
                    : "primary"
                }
                onClick={handleSubmit}
                disabled={
                  isLoadingAllCameraIds ||
                  (!tempAllCameraSelected &&
                    !Object.values(checkedCamerasState).some(Boolean))
                }
              />

              <AdminFormFieldSubmit
                customWrapperClass={`col-span-6${Object.values(checkedCamerasState).filter(
                  (item: boolean) => item
                ).length === 0 && !tempAllCameraSelected
                    ? " hidden"
                    : ""
                  }`}
                label={tMain("cancel")}
                type="button"
                variant="danger-transparent"
                onClick={() => hideModal("cross-button")}
              />
            </>
          )}
        </form>
      </FTModal>

      {/* Hierarchy Group Selector - separate modal */}
      {accountHasGroupHierarchyEnabled === "1" && (
        <>
          {modalsState.showHierarchyGroupSelector && (
            <HierarchyGroupSelector
              initiallySelectedGroups={tempFilteredGroupData
                ?.filter((item) => item.checked)
                .map((item) => item.groupid)}
              onSelectionChange={(selectedGroups: string[]) => {
                if (APP_CONFIG.DEBUG.DISPLAY_VERBOSE)
                  console.log("VALUES RECEIVED:", selectedGroups);
                console.log(
                  "GROUP",
                  selectedGroups.map((grp) => {
                    return {
                      groupid: grp,
                      checked: true,
                      is_hgroup: true,
                    };
                  })
                );
                if (debugComponent)
                  console.log(
                    "[UPDATE]",
                    "setting on hierarchy group selection"
                  );
                setTempFilteredGroupData((prev) => {
                  let selected = [...selectedGroups];
                  const updated = prev.map((item) => {
                    const isAvailable = selectedGroups.includes(item?.groupid);
                    if (isAvailable) {
                      selected = selected.filter((val) => val != item.groupid);
                      return {
                        groupid: item?.groupid,
                        description: item?.description,
                        checked: true,
                        is_hgroup: true,
                      };
                    }
                    return {
                      groupid: item?.groupid,
                      description: item?.description,
                      checked: false,
                      is_hgroup: item?.is_hgroup,
                    };
                  });
                  const newSelected = selected.map((grp) => {
                    return {
                      groupid: grp,
                      description: "",
                      checked: true,
                      is_hgroup: true,
                    };
                  });
                  return [...updated, ...newSelected];
                });
                // setTempFilteredGroupData(
                //   selectedGroups.map((grp) => {
                //     return {
                //       groupid: grp,
                //       checked: true,
                //       is_hgroup: true,
                //     };
                //   })
                // );
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default ModalGroupCameraSelector;